/**
 * ClickPickerBridge — Thin React wrapper that bridges InteractionController to R3F.
 *
 * Responsibilities:
 *   - useEffect subscriptions to selection events (delegates to controller)
 *   - Arrow/Escape key event listener (routes through CameraIntelligence)
 *   - CameraIntelligence tileChange subscription for selection management
 *   - Renders ClickPicker with reactive store props
 *
 * The bridge owns NO logic — all interaction logic lives in InteractionController
 * and CameraIntelligence.
 */
import React, { useEffect, useCallback } from 'react';
import * as THREE from 'three';
import type { StoreApi, UseBoundStore } from 'zustand';
import type { GPUParticleStoreState, GPUParticleStoreActions } from '../stores/useGPUParticleStore';
import { EngineRuntime } from '@arkturian/wave-engine/core/EngineRuntime';
import { InteractionController } from '@arkturian/wave-engine/interaction/InteractionController';
import ClickPicker from '@arkturian/wave-engine/camera/ClickPicker';

type StoreType = UseBoundStore<StoreApi<GPUParticleStoreState & GPUParticleStoreActions>>;

interface ClickPickerBridgeProps {
    controller: InteractionController;
    meshRef: React.RefObject<THREE.InstancedMesh>;
    store: StoreType;
    uniformsRef: React.MutableRefObject<any>;
}

const ClickPickerBridge: React.FC<ClickPickerBridgeProps> = ({
    controller,
    meshRef,
    store,
    uniformsRef,
}) => {
    const particleCount = store((s) => s.particleCount);
    const depthDirection = store((s) => s.depthDirection);
    const dataMode = store((s) => s.dataMode);
    const dataReveal = store((s) => s.dataReveal);

    // Subscribe to selection events — delegate to controller
    useEffect(() => {
        const selMgr = controller.selectionManager;

        const unsubSelect = selMgr.on('select', (sel) => {
            if (meshRef.current && uniformsRef.current) {
                controller.onSelect(sel, meshRef.current, uniformsRef.current, store);
            }
        });

        const unsubInfo = selMgr.on('infoReady', (sel) => {
            if (uniformsRef.current) {
                controller.onInfoReady(sel, uniformsRef.current);
            }
        });

        const unsubDeselect = selMgr.on('deselect', () => {
            if (meshRef.current && uniformsRef.current) {
                controller.onDeselect(meshRef.current, uniformsRef.current);
            }
        });

        return () => {
            unsubSelect();
            unsubInfo();
            unsubDeselect();
        };
    }, [controller, meshRef, uniformsRef, store]);

    // Subscribe to CameraIntelligence tileChange for selection management
    useEffect(() => {
        const ci = EngineRuntime.getInstance().cameraIntelligence;
        if (!ci) return;

        const unsub = ci.on('tileChange', (index: number) => {
            if (!meshRef.current) return;
            const layoutAttr = meshRef.current.geometry.getAttribute('aLayout') as THREE.InstancedBufferAttribute;
            const quatAttr = meshRef.current.geometry.getAttribute('aQuaternion') as THREE.InstancedBufferAttribute;
            const quat = new THREE.Quaternion(
                quatAttr.getX(index), quatAttr.getY(index),
                quatAttr.getZ(index), quatAttr.getW(index),
            );
            const normal = new THREE.Vector3(0, 0, 1).applyQuaternion(quat);
            const pos = new THREE.Vector3(
                layoutAttr.getX(index), layoutAttr.getY(index), layoutAttr.getZ(index),
            );
            controller.selectionManager.select(index, pos, normal);
        });

        return unsub;
    }, [controller, meshRef]);

    // Arrow keys: Left/Right = sequential, Up/Down = spatial, Escape = overview
    // All navigation routes exclusively through CameraIntelligence
    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            const ci = EngineRuntime.getInstance().cameraIntelligence;
            if (!ci) return;

            // Escape: fly to overview + deselect
            if (e.key === 'Escape') {
                e.preventDefault();
                ci.flyToOverview();
                controller.selectionManager.deselect();
                return;
            }

            // Forward/Back: sequential camera fly
            if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
                e.preventDefault();
                const dir = e.key === 'ArrowRight' ? 1 : -1;
                ci.navigateSequential(dir as 1 | -1);
                return;
            }

            // Up/Down: spatial neighbor navigation (requires current tile)
            if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                e.preventDefault();
                if (ci.currentTileIndex !== null) {
                    const spatialDir = e.key === 'ArrowUp' ? 'up' : 'down';
                    ci.navigateSpatial(spatialDir);
                }
            }
        };

        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [controller]);

    // Single click: trigger ripple via controller
    const handleTap = useCallback(
        (index: number, hitUV: { x: number; y: number }) => {
            controller.handleTap(index, hitUV);
        },
        [controller],
    );

    return (
        <ClickPicker
            meshRef={meshRef}
            particleCount={particleCount}
            depthDirection={depthDirection}
            dataMode={dataMode}
            dataReveal={dataReveal}
            onTap={handleTap}
        />
    );
};

export default ClickPickerBridge;
